/**
 * Work-email-only signup (owner decision 2026-09-08).
 *
 * A tenant is a company, so the address that creates one must belong to a
 * company domain. This blocks the consumer mailbox providers and the common
 * disposable-address services. It is a LIST, not a reputation service: an
 * unlisted throwaway domain gets through, and that is accepted.
 *
 * Only tenant CREATION uses this (auth signup). Invites and join flows do not:
 * a colleague's gmail invited into an existing workspace is the owner's call.
 *
 * Browser-safe: no Node globals at module eval (packages/shared is bundled
 * into apps/web).
 */
export const WORK_EMAIL_REJECTION_MESSAGE = 'Please use your work email address';
/**
 * Matched as a SUFFIX on label boundaries, so `mail.yahoo.co.uk` is caught by
 * `yahoo.co.uk` and `something.gmail.com` by `gmail.com`.
 */
export const BLOCKED_EMAIL_DOMAINS = [
    // Consumer mailbox providers
    'gmail.com', 'googlemail.com',
    'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.in', 'yahoo.fr', 'yahoo.de', 'yahoo.ca',
    'yahoo.com.au', 'yahoo.co.jp', 'yahoo.es', 'yahoo.it', 'ymail.com', 'rocketmail.com',
    'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.it', 'hotmail.es', 'hotmail.co.in',
    'outlook.com', 'outlook.in', 'outlook.co.uk', 'outlook.de', 'outlook.fr', 'outlook.es',
    'live.com', 'live.co.uk', 'live.in', 'live.fr', 'live.de', 'live.ca', 'msn.com',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com', 'aim.com',
    'proton.me', 'protonmail.com', 'protonmail.ch', 'pm.me',
    'yandex.com', 'yandex.ru', 'ya.ru',
    'zoho.com', 'zohomail.com', 'zohomail.in',
    'gmx.com', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch',
    'mail.com', 'email.com',
    'rediffmail.com',
    // Disposable / temporary address services
    'mailinator.com', 'guerrillamail.com', 'guerrillamail.info', 'guerrillamail.net', 'sharklasers.com',
    '10minutemail.com', '10minutemail.net', 'tempmail.com', 'temp-mail.org', 'temp-mail.io',
    'throwawaymail.com', 'yopmail.com', 'yopmail.fr', 'dispostable.com', 'trashmail.com',
    'trash-mail.com', 'getnada.com', 'maildrop.cc', 'fakeinbox.com', 'mohmal.com',
    'emailondeck.com', 'mintemail.com', 'discard.email', 'spamgourmet.com', 'mailnesia.com',
    'inboxkitten.com', 'burnermail.io', '33mail.com', 'tempail.com', 'moakt.com',
    'tmpmail.net', 'tmail.ws', '1secmail.com', '1secmail.net', 'mail.tm', 'mailsac.com',
    'spam4.me', 'harakirimail.com', 'mailcatch.com', 'jetable.org', 'anonbox.net',
    'mytemp.email', 'luxusmail.org', 'emailfake.com', 'crazymailing.com', 'dropmail.me',
    'tempmailo.com', 'tempmailaddress.com', 'mail7.io', 'minutemail.com', 'email-fake.com',
    'generator.email', 'throwam.com', 'mailtemp.net', 'tempr.email', 'tempinbox.com',
];
const BLOCKED = new Set(BLOCKED_EMAIL_DOMAINS);
/** Lower-cased domain part of an address, or null when there is none. */
export function emailDomain(email) {
    const trimmed = email.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0 || at === trimmed.length - 1)
        return null;
    const domain = trimmed.slice(at + 1);
    return domain.includes('.') ? domain : null;
}
/**
 * true when the address is on a domain that is not a known consumer or
 * disposable provider. An address with no parseable domain is NOT a work
 * email. Plus-addressing (`me+tag@…`) is in the local part and is ignored.
 */
export function isWorkEmail(email) {
    const domain = emailDomain(email);
    if (!domain)
        return false;
    const labels = domain.split('.');
    for (let i = 0; i < labels.length - 1; i++) {
        if (BLOCKED.has(labels.slice(i).join('.')))
            return false;
    }
    return true;
}
//# sourceMappingURL=work-email.js.map